import * as Sentry from '@sentry/react'
import { HttpClient } from '../api/client'
import { TokenManager } from '../auth/tokenManager'
import type {
  PartnerReferralsResponse,
  AttributeReferralResponse,
  PartnerReferralStats,
} from '@bandos/shared'

// Local storage key for referral code
const REFERRAL_CODE_KEY = 'bandos_referral_code'
const REFERRAL_EXPIRY_KEY = 'bandos_referral_expiry'

// Attribution window: 30 days in milliseconds
const ATTRIBUTION_WINDOW_MS = 30 * 24 * 60 * 60 * 1000

export class ReferralService {
  /**
   * Store a referral code in localStorage with 30-day expiration
   */
  static storeReferralCode(code: string): void {
    const normalizedCode = code.trim().toUpperCase()
    const expiryTime = Date.now() + ATTRIBUTION_WINDOW_MS

    localStorage.setItem(REFERRAL_CODE_KEY, normalizedCode)
    localStorage.setItem(REFERRAL_EXPIRY_KEY, expiryTime.toString())
  }

  /**
   * Get the stored referral code if it hasn't expired
   */
  static getStoredReferralCode(): string | null {
    const code = localStorage.getItem(REFERRAL_CODE_KEY)
    const expiryStr = localStorage.getItem(REFERRAL_EXPIRY_KEY)

    if (!code || !expiryStr) {
      return null
    }

    const expiry = parseInt(expiryStr, 10)
    if (Date.now() > expiry) {
      // Code has expired, clear it
      this.clearReferralCode()
      return null
    }

    return code
  }

  /**
   * Clear the stored referral code
   */
  static clearReferralCode(): void {
    localStorage.removeItem(REFERRAL_CODE_KEY)
    localStorage.removeItem(REFERRAL_EXPIRY_KEY)
  }

  /**
   * Check URL for referral code parameter and store if present
   * Call this on app mount or on login/signup page mount
   */
  static checkAndStoreReferralFromUrl(): string | null {
    const urlParams = new URLSearchParams(window.location.search)
    const refCode = urlParams.get('ref')

    if (refCode) {
      this.storeReferralCode(refCode)
      // Clean up URL (optional - removes ?ref= from URL)
      const url = new URL(window.location.href)
      url.searchParams.delete('ref')
      window.history.replaceState({}, '', url.toString())
      return refCode
    }

    return this.getStoredReferralCode()
  }

  /**
   * Attribute a referral after user signup/login
   * Should be called after user successfully logs in for the first time
   */
  static async attributeReferral(): Promise<AttributeReferralResponse | null> {
    const code = this.getStoredReferralCode()

    if (!code) {
      return null
    }

    try {
      const headers = await TokenManager.getAuthHeaders()
      const response = await HttpClient.post<AttributeReferralResponse>(
        '/referral/attribute',
        { referral_code: code },
        headers
      )

      // Clear the code after attribution attempt (whether successful or not)
      // We only want to attempt attribution once
      this.clearReferralCode()

      return response.data
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'service', action: 'attribute_referral' },
      })
      // Don't clear on error - might be a network issue
      return null
    }
  }

  /**
   * Validate a referral code (public endpoint)
   */
  static async validateReferralCode(code: string): Promise<boolean> {
    try {
      const response = await HttpClient.get<{ valid: boolean }>(
        `/public/referral/validate?referral_code=${encodeURIComponent(code)}`
      )
      return response.data?.valid ?? false
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'service', action: 'validate_referral_code' },
      })
      return false
    }
  }

  /**
   * Get partner's referrals (for partner dashboard)
   */
  static async getPartnerReferrals(): Promise<PartnerReferralsResponse> {
    const headers = await TokenManager.getAuthHeaders()
    if (!headers.Authorization) {
      throw new Error('Authentication required')
    }
    const response = await HttpClient.get<PartnerReferralsResponse>('/partner/referrals', headers)
    if (!response.data) {
      throw new Error(response.error?.message ?? 'Failed to fetch referrals')
    }
    return response.data
  }

  /**
   * Get partner's stats only (lightweight)
   */
  static async getPartnerStats(): Promise<{ stats: PartnerReferralStats }> {
    const headers = await TokenManager.getAuthHeaders()
    if (!headers.Authorization) {
      throw new Error('Authentication required')
    }
    const response = await HttpClient.get<{ stats: PartnerReferralStats }>(
      '/partner/stats',
      headers
    )
    if (!response.data) {
      throw new Error(response.error?.message ?? 'Failed to fetch stats')
    }
    return response.data
  }
}
